import React, { useState } from "react";
import Modal from "@mui/material/Modal";
import {
    Button,
    Checkbox,
    FormControl,
    Grid,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
} from "@mui/material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import styled from "styled-components";
import ConfigCategoryToggle from "src/scenes/dashboard/work-analytics/deployments-config/config-category-toggle";
import SettingsIcon from "@mui/icons-material/Settings";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import { Link as RouteLink } from "react-router-dom";

const SubtitleText = styled.span`
    font-size: 12px;
    font-weight: 400;
    color: ${({ theme }) => theme.palette.primary.main};
`;

const SubtitleTextLight = styled.span`
    font-size: 12px;
    font-weight: 300;
    max-width: 368px;
    color: ${({ theme }) => theme.palette.secondary.main};
`;

const TitleText = styled.p`
    font-size: 14px;
    margin: 0;
`;

const DashboardButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    margin-top: 13px;
    z-index: 10;
`;

const AllConfigLink = styled(RouteLink)`
    font-size: 12px;
    font-weight: 300;
    line-height: 20px;
    font-style: normal;
    text-decoration: none;
    &:hover {
        text-decoration: underline;
    }
`;

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "auto",
    height: "auto",
    bgcolor: "background.paper",
    borderRadius: 4,
    boxShadow: 24,
    p: 4,
};

const DeploymentsConfigModal = ({ open, handleClose, isDemo, isTeams, teamId, fetchAction }) => {
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [data, setData] = useState({});
    const [selectedCategory, setSelectedCategory] = useState("general");
    const [deploymentsSelected, setDeploymentsSelected] = useState(false);
    const [releasesSelected, setReleasesSelected] = useState(false);
    const [pipelinesSelected, setPipelinesSelected] = useState(false);
    const [webhookSelected, setWebhookSelected] = useState(false);
    const [repo, setRepo] = useState(undefined);
    const [allRepos, setAllRepos] = useState([]);
    const [configurationDataPerRepo, setConfigurationDataPerRepo] = useState({});
    const [initialConfigurationDataPerRepo, setInitialConfigurationDataPerRepo] = useState({});
    const [configurationDataGeneral, setConfigurationDataGeneral] = useState({});

    const handleChange = (event) => {
        setRepo(event.target.value);
        setDeploymentsSelected(false);
        setReleasesSelected(false);
        setPipelinesSelected(false);
        setWebhookSelected(false);
    };

    const getConfigurationSettings = async () => {
        let response;
        if (isDemo) {
            response = (await import("./deployments-config-mock-data")).default;
        } else if (isTeams) {
            response = await request(`/api/data/teams/${teamId}/deployment-configuration`, {
                method: "GET",
            });
        } else {
            response = await request(`/api/data/deployment-configuration`, { method: "GET" });
        }
        setData(response);
    };

    const handleGetConfiguration = async () => {
        try {
            getConfigurationSettings();
        } catch (e) {
            setMessage(`Error retrieving deployment configuration. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    const setConfigurationSettings = async () => {
        if (selectedCategory === "general") {
            if (teamId) {
                await request(`/api/data/teams/${teamId}/deployment-configuration`, {
                    method: "POST",
                    body: JSON.stringify({
                        "adadot-general-configuration-settings": configurationDataGeneral,
                    }),
                });
            }
            await request(`/api/data/deployment-configuration`, {
                method: "POST",
                body: JSON.stringify({
                    "adadot-general-configuration-settings": configurationDataGeneral,
                }),
            });
        }
        if (selectedCategory === "repo") {
            const alteredRepos = Object.keys(configurationDataPerRepo).reduce((acc, configRepo) => {
                if (
                    JSON.stringify(configurationDataPerRepo[configRepo]) !==
                    JSON.stringify(initialConfigurationDataPerRepo[configRepo])
                ) {
                    const { defaultConfig, ...config } = configurationDataPerRepo[configRepo];
                    acc[configRepo] = config;
                }
                return acc;
            }, {});
            if (Object.keys(alteredRepos).length === 0) {
                return;
            }
            if (teamId) {
                await request(`/api/data/teams/${teamId}/deployment-configuration`, {
                    method: "POST",
                    body: JSON.stringify(alteredRepos),
                });
            }
            await request(`/api/data/deployment-configuration`, {
                method: "POST",
                body: JSON.stringify(alteredRepos),
            });
        }
        setMessage(`Deployment configuration saved successfully`);
        setSeverity("success");
        setIsToastTriggered(true);
        handleGetConfiguration();
        fetchAction?.refetch();
        handleClose();
    };

    const handleSubmitConfiguration = async () => {
        try {
            setConfigurationSettings();
        } catch (e) {
            setMessage(`Error saving deployment configuration. Please try again.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    React.useEffect(() => {
        handleGetConfiguration();
    }, []);

    React.useEffect(() => {
        setConfigurationDataGeneral(data && data.general);
        setConfigurationDataPerRepo(data && data.perRepo);
        if (data && data.perRepo) {
            setInitialConfigurationDataPerRepo(JSON.parse(JSON.stringify(data.perRepo)));
        }
    }, [data]);

    React.useEffect(() => {
        if (data && data.perRepo) {
            const outerKeys = Object.keys(data.perRepo);
            setAllRepos(outerKeys);
            if (!repo) {
                setRepo(allRepos[0]);
            }
        }
    }, [data]);

    React.useEffect(() => {
        if (selectedCategory === "general") {
            const obj = data && data[selectedCategory];
            if (obj && Object.keys(obj).includes("deployments")) setDeploymentsSelected(true);
            if (obj && Object.keys(obj).includes("releases")) setReleasesSelected(true);
            if (obj && Object.keys(obj).includes("pipelines")) setPipelinesSelected(true);
            if (obj && Object.keys(obj).includes("webhook")) setWebhookSelected(true);
        }
        if (selectedCategory === "repo") {
            if (!repo) {
                setDeploymentsSelected(false);
                setReleasesSelected(false);
                setPipelinesSelected(false);
                setWebhookSelected(false);
            } else {
                const obj = data && data.perRepo && data.perRepo[repo];
                const innerObjKeys = Object.keys(obj);
                if (innerObjKeys) {
                    if (innerObjKeys.includes("deployments")) setDeploymentsSelected(true);
                    if (innerObjKeys.includes("releases")) setReleasesSelected(true);
                    if (innerObjKeys.includes("pipelines")) setPipelinesSelected(true);
                    if (innerObjKeys.includes("webhook")) setWebhookSelected(true);
                }
            }
        }
    }, [data, selectedCategory, repo]);

    React.useEffect(() => {
        if (webhookSelected) {
            if (selectedCategory === "general") {
                setConfigurationDataGeneral({
                    ...configurationDataGeneral,
                    webhook: true,
                });
            }
            if (selectedCategory === "repo") {
                setConfigurationDataPerRepo({
                    ...configurationDataPerRepo,
                    [repo]: {
                        ...configurationDataPerRepo[repo],
                        webhook: true,
                    },
                });
            }
        }
        if (!webhookSelected) {
            if (selectedCategory === "general") {
                setConfigurationDataGeneral({
                    ...configurationDataGeneral,
                    webhook: false,
                });
            }
            if (selectedCategory === "repo") {
                setConfigurationDataPerRepo({
                    ...configurationDataPerRepo,
                    [repo]: {
                        ...configurationDataPerRepo[repo],
                        webhook: false,
                    },
                });
            }
        }
    }, [webhookSelected]);

    // Handle delete and replace from object
    React.useEffect(() => {
        if (selectedCategory === "general") {
            if (
                !deploymentsSelected &&
                configurationDataGeneral &&
                configurationDataGeneral.deployments
            ) {
                delete configurationDataGeneral.deployments;
            } else if (
                deploymentsSelected &&
                configurationDataGeneral &&
                !configurationDataGeneral.deployments
            ) {
                setConfigurationDataGeneral({
                    ...configurationDataGeneral,
                    deployments: {
                        environment: null,
                    },
                });
            }
            if (
                !releasesSelected &&
                configurationDataGeneral &&
                configurationDataGeneral.releases
            ) {
                delete configurationDataGeneral.releases;
            } else if (
                releasesSelected &&
                configurationDataGeneral &&
                !configurationDataGeneral.releases
            ) {
                setConfigurationDataGeneral({
                    ...configurationDataGeneral,
                    releases: {
                        name: null,
                    },
                });
            }
            if (
                !pipelinesSelected &&
                configurationDataGeneral &&
                configurationDataGeneral.pipelines
            ) {
                delete configurationDataGeneral.pipelines;
            } else if (
                pipelinesSelected &&
                configurationDataGeneral &&
                !configurationDataGeneral.pipelines
            ) {
                setConfigurationDataGeneral({
                    ...configurationDataGeneral,
                    pipelines: {
                        branch: null,
                        workflow: null,
                    },
                });
            }
        }
        if (selectedCategory === "repo") {
            if (
                !deploymentsSelected &&
                configurationDataPerRepo &&
                configurationDataPerRepo[repo] &&
                configurationDataPerRepo[repo].deployments
            ) {
                delete configurationDataPerRepo[repo].deployments;
            } else if (
                deploymentsSelected &&
                configurationDataPerRepo &&
                configurationDataPerRepo[repo] &&
                !configurationDataPerRepo[repo].deployments
            ) {
                setConfigurationDataPerRepo({
                    ...configurationDataPerRepo,
                    [repo]: {
                        ...configurationDataPerRepo[repo],
                        deployments: {
                            environment: null,
                        },
                    },
                });
            }
            if (
                !releasesSelected &&
                configurationDataPerRepo &&
                configurationDataPerRepo[repo] &&
                configurationDataPerRepo[repo].releases
            ) {
                delete configurationDataPerRepo[repo].releases;
            } else if (
                releasesSelected &&
                configurationDataPerRepo &&
                configurationDataPerRepo[repo] &&
                !configurationDataPerRepo[repo].releases
            ) {
                setConfigurationDataPerRepo({
                    ...configurationDataPerRepo,
                    [repo]: {
                        ...configurationDataPerRepo[repo],
                        releases: {
                            name: null,
                        },
                    },
                });
            }
            if (
                !pipelinesSelected &&
                configurationDataPerRepo &&
                configurationDataPerRepo[repo] &&
                configurationDataPerRepo[repo].pipelines
            ) {
                delete configurationDataPerRepo[repo].pipelines;
            } else if (
                pipelinesSelected &&
                configurationDataPerRepo &&
                configurationDataPerRepo[repo] &&
                !configurationDataPerRepo[repo].pipelines
            ) {
                setConfigurationDataPerRepo({
                    ...configurationDataPerRepo,
                    [repo]: {
                        ...configurationDataPerRepo[repo],
                        pipelines: {
                            branch: null,
                            workflow: null,
                        },
                    },
                });
            }
        }
    }, [deploymentsSelected, releasesSelected, pipelinesSelected]);

    const handleCheckboxChange = (event) => {
        if (event.target.value === "deployments") {
            setDeploymentsSelected(!deploymentsSelected);
        }
        if (event.target.value === "releases") {
            setReleasesSelected(!releasesSelected);
        }
        if (event.target.value === "pipelines") {
            setPipelinesSelected(!pipelinesSelected);
        }
        if (event.target.value === "webhook") {
            setWebhookSelected(!webhookSelected);
        }
    };

    const handleReset = () => {
        if (selectedCategory === "general") {
            setDeploymentsSelected(true);
            setReleasesSelected(false);
            setPipelinesSelected(false);
            setWebhookSelected(false);
            setConfigurationDataGeneral({
                deployments: {
                    environment: null,
                },
            });
        }
        if (selectedCategory === "repo" && repo) {
            setDeploymentsSelected(true);
            setReleasesSelected(false);
            setPipelinesSelected(false);
            setWebhookSelected(false);
            setConfigurationDataPerRepo({
                ...configurationDataPerRepo,
                [repo]: {
                    deployments: {
                        environment: null,
                    },
                },
            });
        }
    };

    return (
        <div>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Stack>
                    <Box sx={style}>
                        <Stack
                            direction="row"
                            style={{ width: "100%" }}
                            justifyContent="space-between"
                        >
                            <Stack direction="column">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    style={{ opacity: "0.8" }}
                                >
                                    <SettingsIcon />
                                    <Typography id="modal-modal-title" variant="h6" component="h2">
                                        Deployments configuration
                                    </Typography>
                                </Stack>
                                <SubtitleText id="modal-modal-description" sx={{ mt: 2 }}>
                                    Edit the parameters of your deployments.
                                </SubtitleText>
                                <AllConfigLink to="/deployments-config-all">
                                    View all configuration settings here
                                </AllConfigLink>
                            </Stack>
                            <DashboardButton
                                variant="outlined"
                                color="secondary"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleReset();
                                }}
                                startIcon={<RestartAltIcon />}
                                style={{
                                    textDecoration: "auto",
                                    textAlign: "center",
                                    paddingRight: "12px",
                                }}
                            >
                                Reset Configuration
                            </DashboardButton>
                        </Stack>
                        <Stack
                            direction="column"
                            sx={{
                                width: "100%",
                                typography: "body1",
                                paddingTop: "26px",
                            }}
                            spacing={2}
                            justifyContent="space-between"
                        >
                            <ConfigCategoryToggle
                                selectedCategory={selectedCategory}
                                setSelectedCategory={setSelectedCategory}
                                setConfigurationDataGeneral={setConfigurationDataGeneral}
                            />
                            {selectedCategory === "repo" && (
                                <FormControl sx={{ minWidth: 120 }} size="small">
                                    <InputLabel id="demo-select-small-label">
                                        Select repository
                                    </InputLabel>
                                    <Select
                                        labelId="demo-select-small-label"
                                        id="demo-select-small"
                                        value={repo}
                                        label="Repo"
                                        onChange={handleChange}
                                    >
                                        {allRepos.map((repository) => {
                                            return (
                                                <MenuItem key={repository} value={repository}>
                                                    {repository}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}
                            <Grid container spacing={2}>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={deploymentsSelected}
                                            onChange={handleCheckboxChange}
                                            value="deployments"
                                            name="set-deployments"
                                            size="small"
                                            inputProps={{ "aria-label": "deployments" }}
                                        />
                                        <TitleText>Deployments*</TitleText>
                                    </Stack>
                                    <Tooltip title="Use % as wildcard">
                                        <TextField
                                            id="outlined-basic"
                                            label="Environment"
                                            variant="outlined"
                                            size="small"
                                            autoComplete="off"
                                            disabled={!deploymentsSelected}
                                            value={
                                                !deploymentsSelected
                                                    ? ""
                                                    : (selectedCategory === "general" &&
                                                          configurationDataGeneral &&
                                                          configurationDataGeneral.deployments &&
                                                          configurationDataGeneral.deployments
                                                              .environment) ||
                                                      (selectedCategory === "repo" &&
                                                          configurationDataPerRepo[repo] &&
                                                          configurationDataPerRepo[repo]
                                                              .deployments &&
                                                          configurationDataPerRepo[repo].deployments
                                                              .environment) ||
                                                      null
                                            }
                                            onChange={(event) => {
                                                if (selectedCategory === "general") {
                                                    setConfigurationDataGeneral({
                                                        ...configurationDataGeneral,
                                                        deployments: {
                                                            environment: event.target.value,
                                                        },
                                                    });
                                                }
                                                if (selectedCategory === "repo") {
                                                    setConfigurationDataPerRepo({
                                                        ...configurationDataPerRepo,
                                                        [repo]: {
                                                            ...configurationDataPerRepo[repo],
                                                            deployments: {
                                                                environment: event.target.value,
                                                            },
                                                        },
                                                    });
                                                }
                                            }}
                                            InputProps={{
                                                sx: {
                                                    fontSize: "12px",
                                                    fontWeight: 300,
                                                    padding: "3.5px",
                                                },
                                            }}
                                            InputLabelProps={{
                                                sx: {
                                                    fontSize: "12px",
                                                    fontWeight: 300,
                                                    padding: "3.5px",
                                                    "&.MuiOutlinedInput-notchedOutline": {
                                                        fontSize: "13px",
                                                    },
                                                },
                                            }}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={releasesSelected}
                                            onChange={handleCheckboxChange}
                                            value="releases"
                                            name="set-releases"
                                            size="small"
                                            inputProps={{ "aria-label": "releases" }}
                                        />
                                        <TitleText>Releases / Tags*</TitleText>
                                    </Stack>
                                    <Stack direction="column" spacing={2}>
                                        <Tooltip title="Use % as wildcard">
                                            <TextField
                                                id="outlined-basic"
                                                label="Tag name"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                disabled={!releasesSelected}
                                                value={
                                                    !releasesSelected
                                                        ? ""
                                                        : (selectedCategory === "general" &&
                                                              configurationDataGeneral &&
                                                              configurationDataGeneral.releases &&
                                                              configurationDataGeneral.releases
                                                                  .name) ||
                                                          (selectedCategory === "repo" &&
                                                              configurationDataPerRepo[repo] &&
                                                              configurationDataPerRepo[repo]
                                                                  .releases &&
                                                              configurationDataPerRepo[repo]
                                                                  .releases.name) ||
                                                          null
                                                }
                                                onChange={(event) => {
                                                    if (selectedCategory === "general") {
                                                        setConfigurationDataGeneral({
                                                            ...configurationDataGeneral,
                                                            releases: {
                                                                name: event.target.value,
                                                            },
                                                        });
                                                    }
                                                    if (selectedCategory === "repo") {
                                                        setConfigurationDataPerRepo({
                                                            ...configurationDataPerRepo,
                                                            [repo]: {
                                                                ...configurationDataPerRepo[repo],
                                                                releases: {
                                                                    name: event.target.value,
                                                                },
                                                            },
                                                        });
                                                    }
                                                }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                        "&.MuiOutlinedInput-notchedOutline": {
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={pipelinesSelected}
                                            onChange={handleCheckboxChange}
                                            value="pipelines"
                                            name="set-pipelines"
                                            size="small"
                                            inputProps={{ "aria-label": "pipelines" }}
                                        />
                                        <TitleText>Pipelines*</TitleText>
                                    </Stack>
                                    <Stack direction="column" spacing={2}>
                                        <Tooltip title="Use % as wildcard">
                                            <TextField
                                                id="outlined-basic"
                                                label="Branch / Reference"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                disabled={!pipelinesSelected}
                                                value={
                                                    !pipelinesSelected
                                                        ? ""
                                                        : (selectedCategory === "general" &&
                                                              configurationDataGeneral &&
                                                              configurationDataGeneral.pipelines &&
                                                              configurationDataGeneral.pipelines
                                                                  .branch) ||
                                                          (selectedCategory === "repo" &&
                                                              configurationDataPerRepo[repo] &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines.branch) ||
                                                          null
                                                }
                                                onChange={(event) => {
                                                    if (selectedCategory === "general") {
                                                        setConfigurationDataGeneral({
                                                            ...configurationDataGeneral,
                                                            pipelines: {
                                                                branch: event.target.value,
                                                                workflow:
                                                                    (configurationDataGeneral &&
                                                                        configurationDataGeneral.pipelines &&
                                                                        configurationDataGeneral
                                                                            .pipelines.workflow) ||
                                                                    undefined,
                                                            },
                                                        });
                                                    }
                                                    if (selectedCategory === "repo") {
                                                        setConfigurationDataPerRepo({
                                                            ...configurationDataPerRepo,
                                                            [repo]: {
                                                                ...configurationDataPerRepo[repo],
                                                                pipelines: {
                                                                    branch: event.target.value,
                                                                    workflow:
                                                                        (configurationDataPerRepo[
                                                                            repo
                                                                        ].pipelines &&
                                                                            configurationDataPerRepo[
                                                                                repo
                                                                            ].pipelines.workflow) ||
                                                                        undefined,
                                                                },
                                                            },
                                                        });
                                                    }
                                                }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                        "&.MuiOutlinedInput-notchedOutline": {
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Use % as wildcard">
                                            <TextField
                                                id="outlined-basic"
                                                label="Workflow"
                                                variant="outlined"
                                                size="small"
                                                autoComplete="off"
                                                disabled={!pipelinesSelected}
                                                value={
                                                    !pipelinesSelected
                                                        ? ""
                                                        : (selectedCategory === "general" &&
                                                              configurationDataGeneral &&
                                                              configurationDataGeneral.pipelines &&
                                                              configurationDataGeneral.pipelines
                                                                  .workflow) ||
                                                          (selectedCategory === "repo" &&
                                                              configurationDataPerRepo[repo] &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines &&
                                                              configurationDataPerRepo[repo]
                                                                  .pipelines.workflow) ||
                                                          null
                                                }
                                                onChange={(event) => {
                                                    if (selectedCategory === "general") {
                                                        setConfigurationDataGeneral({
                                                            ...configurationDataGeneral,
                                                            pipelines: {
                                                                branch:
                                                                    (configurationDataGeneral.pipelines &&
                                                                        configurationDataGeneral
                                                                            .pipelines.branch) ||
                                                                    undefined,
                                                                workflow: event.target.value,
                                                            },
                                                        });
                                                    }
                                                    if (selectedCategory === "repo") {
                                                        setConfigurationDataPerRepo({
                                                            ...configurationDataPerRepo,
                                                            [repo]: {
                                                                ...configurationDataPerRepo[repo],
                                                                pipelines: {
                                                                    branch:
                                                                        (configurationDataPerRepo[
                                                                            repo
                                                                        ].pipelines &&
                                                                            configurationDataPerRepo[
                                                                                repo
                                                                            ].pipelines.branch) ||
                                                                        undefined,
                                                                    workflow: event.target.value,
                                                                },
                                                            },
                                                        });
                                                    }
                                                }}
                                                InputProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                    },
                                                }}
                                                InputLabelProps={{
                                                    sx: {
                                                        fontSize: "12px",
                                                        fontWeight: 300,
                                                        padding: "3.5px",
                                                        "&.MuiOutlinedInput-notchedOutline": {
                                                            fontSize: "13px",
                                                        },
                                                    },
                                                }}
                                            />
                                        </Tooltip>
                                    </Stack>
                                </Grid>
                                <Grid item>
                                    <Stack direction="row" alignItems="center">
                                        <Checkbox
                                            checked={webhookSelected}
                                            onChange={handleCheckboxChange}
                                            value="webhook"
                                            name="set-webhook"
                                            size="small"
                                            inputProps={{ "aria-label": "webhook" }}
                                        />
                                        <TitleText>Webhook</TitleText>
                                    </Stack>
                                    <p
                                        style={{
                                            fontSize: "11px",
                                            opacity: "0.5",
                                            marginBottom: 0,
                                        }}
                                    >
                                        No extra settings here.
                                    </p>
                                    <Link
                                        href="https://docs.adadot.com/Integrations/webhooks/"
                                        target="_blank"
                                        style={{ fontSize: "11px", opacity: "0.9" }}
                                    >
                                        How to configure webhooks
                                    </Link>
                                </Grid>
                            </Grid>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={2}
                                style={{ width: "100%", marginTop: "30px" }}
                            >
                                <SubtitleTextLight id="modal-modal-description" sx={{ mt: 2 }}>
                                    *Input the name of the env/tag/release/branch/workflow, or use %
                                    to select all available.
                                </SubtitleTextLight>
                                <Stack direction="row" spacing={2}>
                                    <Button color="secondary" size="medium" onClick={handleClose}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        onClick={isDemo ? handleClose : handleSubmitConfiguration}
                                    >
                                        Save Configuration
                                    </Button>
                                </Stack>
                            </Stack>
                        </Stack>
                    </Box>
                </Stack>
            </Modal>
        </div>
    );
};

export default DeploymentsConfigModal;
